import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './About.module.scss';

const MAX_TILT = 16;
const PORTRAIT_DEPTH = 52;
const TEXT_TILT_Y = 18;
const TEXT_TILT_X = 12;
const CARD_DRIFT = 20;
const TEXT_DRIFT = 12;
const LERP = 0.07;
const LERP_STOP_THRESHOLD = 0.0002;

const About = () => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const portraitRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const targetX = useRef(0);
  const targetY = useRef(0);
  const currentX = useRef(0);
  const currentY = useRef(0);
  const rafId = useRef<number>(0);
  const running = useRef(false);

  useEffect(() => {
    const applyTilt = (x: number, y: number) => {
      if (cardRef.current) {
        cardRef.current.style.transform = `perspective(900px) translateX(${x * CARD_DRIFT}px) translateY(${y * CARD_DRIFT * 0.65}px) rotateY(${x * MAX_TILT * 2}deg) rotateX(${-y * MAX_TILT}deg)`;
      }
      if (portraitRef.current) {
        portraitRef.current.style.transform = `translateZ(${PORTRAIT_DEPTH}px)`;
      }
      if (textRef.current) {
        textRef.current.style.transform = `perspective(1000px) translateX(${x * TEXT_DRIFT}px) translateY(${y * TEXT_DRIFT * 0.65}px) rotateY(${x * TEXT_TILT_Y}deg) rotateX(${-y * TEXT_TILT_X}deg)`;
      }
    };

    const tick = () => {
      currentX.current += (targetX.current - currentX.current) * LERP;
      currentY.current += (targetY.current - currentY.current) * LERP;
      applyTilt(currentX.current, currentY.current);

      const converged =
        Math.abs(targetX.current - currentX.current) < LERP_STOP_THRESHOLD &&
        Math.abs(targetY.current - currentY.current) < LERP_STOP_THRESHOLD;

      if (converged) {
        running.current = false;
      } else {
        rafId.current = requestAnimationFrame(tick);
      }
    };

    const startLoop = () => {
      if (!running.current) {
        running.current = true;
        rafId.current = requestAnimationFrame(tick);
      }
    };

    const onMove = (e: MouseEvent) => {
      targetX.current = e.clientX / window.innerWidth - 0.5;
      targetY.current = e.clientY / window.innerHeight - 0.5;
      startLoop();
    };

    const onLeave = () => {
      targetX.current = 0;
      targetY.current = 0;
      startLoop();
    };

    // Skip mouse tilt on touch-primary devices (mobile / tablet)
    if (!window.matchMedia('(pointer: fine)').matches) return;

    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafId.current);
    };
  }, []);

  return (
    <div className={styles.about}>
      <div className={styles.text} ref={textRef}>
        <p className={styles.bio}>
          {t('about.bio1.before')}
          <span className={styles.highlight}>{t('about.bio1.highlight')}</span>
          {t('about.bio1.after')}
        </p>
      </div>

      <div className={styles.cardWrapper}>
        <div className={styles.card} ref={cardRef}>
          <div className={styles.portrait} ref={portraitRef}>
            <span className={styles.portraitLabel}>{t('about.portraitLabel')}</span>
          </div>

          <p className={styles.chips}>
            {t('about.chip.location')} · {t('about.chip.collab')} · {t('about.chip.remote')} · {t('about.chip.shipped')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;
